/**
 * Centralized Theme System
 * Provides consistent styling across all components
 * Eliminates duplication and ensures theme consistency
 */

export type ThemeMode = "normal" | "konami";

export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryHover: string;
  primaryActive: string;

  // Secondary colors
  secondary: string;
  secondaryHover: string;

  // Text colors
  text: {
    primary: string;
    secondary: string;
    muted: string;
    inverse: string;
  };

  // Background colors
  background: {
    primary: string;
    secondary: string;
    card: string;
    hover: string;
  };

  // Border colors
  border: {
    primary: string;
    secondary: string;
    hover: string;
  };

  // Shadow colors
  shadow: {
    primary: string;
    hover: string;
    glow: string;
  };

  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;
}

export const themes: Record<ThemeMode, ThemeColors> = {
  normal: {
    primary: "rgb(34, 197, 94)", // green-500
    primaryHover: "rgb(22, 163, 74)", // green-600
    primaryActive: "rgb(21, 128, 61)", // green-700

    secondary: "rgb(34, 197, 94)", // green-500
    secondaryHover: "rgb(22, 163, 74)", // green-600

    text: {
      primary: "rgb(34, 197, 94)", // green-400
      secondary: "rgb(134, 239, 172)", // green-300
      muted: "rgba(34, 197, 94, 0.7)", // green-400/70
      inverse: "rgb(0, 0, 0)", // black
    },

    background: {
      primary: "rgb(0, 0, 0)", // black
      secondary: "rgb(20, 83, 45)", // green-950
      card: "rgb(20, 83, 45)", // green-950
      hover: "rgb(22, 101, 52)", // green-900
    },

    border: {
      primary: "rgb(34, 197, 94)", // green-400
      secondary: "rgb(21, 128, 61)", // green-700
      hover: "rgb(134, 239, 172)", // green-300
    },

    shadow: {
      primary: "rgba(34, 197, 94, 0.2)", // green-400/20
      hover: "rgba(34, 197, 94, 0.4)", // green-400/40
      glow: "0 0 15px rgba(34, 197, 94, 0.4)", // green glow
    },

    success: "rgb(34, 197, 94)", // green-500
    warning: "rgb(234, 179, 8)", // yellow-500
    error: "rgb(239, 68, 68)", // red-500
    info: "rgb(59, 130, 246)", // blue-500
  },

  konami: {
    primary: "rgb(0, 255, 255)", // cyan
    primaryHover: "rgb(138, 43, 226)", // purple
    primaryActive: "rgb(255, 0, 255)", // magenta

    secondary: "rgb(138, 43, 226)", // purple
    secondaryHover: "rgb(255, 0, 255)", // magenta

    text: {
      primary: "rgb(0, 255, 255)", // cyan
      secondary: "rgba(0, 255, 255, 0.8)", // cyan/80
      muted: "rgba(0, 255, 255, 0.7)", // cyan/70
      inverse: "rgb(0, 0, 0)", // black
    },

    background: {
      primary: "rgb(10, 10, 10)", // very dark
      secondary: "rgb(17, 17, 17)", // dark gray
      card: "rgb(17, 17, 17)", // dark gray
      hover: "rgb(34, 34, 34)", // lighter gray
    },

    border: {
      primary: "rgb(0, 255, 255)", // cyan
      secondary: "rgba(0, 255, 255, 0.3)", // cyan/30
      hover: "rgb(255, 0, 255)", // magenta
    },

    shadow: {
      primary: "rgba(0, 255, 255, 0.2)", // cyan/20
      hover: "rgba(0, 255, 255, 0.4)", // cyan/40
      glow: "0 0 15px rgba(0, 255, 255, 0.6)", // cyan glow
    },

    success: "rgb(0, 255, 255)", // cyan
    warning: "rgb(255, 255, 0)", // yellow
    error: "rgb(255, 0, 255)", // magenta
    info: "rgb(138, 43, 226)", // purple
  },
};

/**
 * Theme utility functions
 */
export const getTheme = (konamiMode: boolean): ThemeColors => {
  return themes[konamiMode ? "konami" : "normal"];
};

/**
 * CSS class generators for consistent styling
 */
export const themeClasses = {
  // Text classes
  text: {
    primary: (konamiMode: boolean) => (konamiMode ? "text-[#00FFFF]" : "text-green-400"),
    secondary: (konamiMode: boolean) => (konamiMode ? "text-[#00FFFF]/80" : "text-green-300"),
    muted: (konamiMode: boolean) => (konamiMode ? "text-[#00FFFF]/70" : "text-green-400/70"),
    accent: (konamiMode: boolean) => (konamiMode ? "text-[#FF8000]" : "text-green-200"),
    inverse: (konamiMode: boolean) => (konamiMode ? "text-black" : "text-black"),
  },

  // Background classes
  background: {
    primary: (konamiMode: boolean) => (konamiMode ? "bg-[#0A0A0A]" : "bg-black"),
    secondary: (konamiMode: boolean) => (konamiMode ? "bg-[#111]" : "bg-green-950"),
    card: (konamiMode: boolean) => (konamiMode ? "bg-[#111]" : "bg-green-950"),
    hover: (konamiMode: boolean) => (konamiMode ? "hover:bg-[#222]" : "hover:bg-green-900"),
  },

  // Border classes
  border: {
    primary: (konamiMode: boolean) => (konamiMode ? "border-[#00FFFF]" : "border-green-400"),
    secondary: (konamiMode: boolean) => (konamiMode ? "border-[#00FFFF]/30" : "border-green-700"),
    hover: (konamiMode: boolean) =>
      konamiMode ? "hover:border-[#FF8000]" : "hover:border-green-300",
  },

  // Shadow classes
  shadow: {
    primary: (konamiMode: boolean) => (konamiMode ? "shadow-[0_0_8px_#00FFFF55]" : "shadow-lg"),
    hover: (konamiMode: boolean) =>
      konamiMode ? "hover:shadow-[0_0_15px_#00FFFF99]" : "hover:shadow-xl",
    glow: (konamiMode: boolean) =>
      konamiMode ? "drop-shadow-[0_0_20px_#00FFFF]" : "drop-shadow-lg",
  },

  // Button classes
  button: {
    primary: (konamiMode: boolean) =>
      konamiMode
        ? "bg-gradient-to-r from-[#00FFFF] to-[#0080FF] text-black hover:brightness-125 focus:ring-[#00FFFF] focus:ring-offset-[#0A0A0A]"
        : "bg-green-700 text-white hover:bg-green-600 focus:ring-green-400 focus:ring-offset-green-950",
    secondary: (konamiMode: boolean) =>
      konamiMode
        ? "bg-[#00FFFF] text-black hover:bg-[#0080FF] hover:text-white focus:ring-[#00FFFF] focus:ring-offset-[#111]"
        : "bg-green-800 text-white hover:bg-green-700 focus:ring-green-400 focus:ring-offset-green-950",
    ghost: (konamiMode: boolean) =>
      konamiMode
        ? "border-[#00FFFF] text-[#00FFFF] hover:bg-[#00FFFF] hover:text-black"
        : "border-green-400 text-green-400 hover:bg-green-400 hover:text-black",
  },

  // Card classes
  card: {
    base: (konamiMode: boolean) =>
      konamiMode
        ? "border-[#00FFFF] bg-[#111] text-[#00FFFF] shadow-[0_0_15px_#00FFFF66] hover:shadow-[0_0_25px_#00FFFF]"
        : "border-green-700 bg-green-950 text-green-200 hover:shadow-lg",
    interactive: (konamiMode: boolean) =>
      konamiMode
        ? "border-[#00FFFF] bg-gradient-to-br from-[#111] to-[#222] shadow-lg shadow-[#00FFFF]/20 hover:shadow-[#00FFFF]/40 hover:scale-[1.02]"
        : "border-green-400 bg-gradient-to-br from-green-950/50 to-black/50 shadow-lg shadow-green-400/20 hover:shadow-green-400/40 hover:scale-[1.02]",
  },

  // Icon classes
  icon: {
    primary: (konamiMode: boolean) => (konamiMode ? "text-[#00FFFF]" : "text-green-400"),
    secondary: (konamiMode: boolean) => (konamiMode ? "text-[#00FFFF]/80" : "text-green-300"),
    hover: (konamiMode: boolean) => (konamiMode ? "hover:text-[#FF8000]" : "hover:text-green-300"),
  },

  // Link classes
  link: {
    primary: (konamiMode: boolean) =>
      konamiMode ? "text-[#00FFFF] hover:text-[#FF8000]" : "text-green-400 hover:text-green-300",
    underline: (konamiMode: boolean) =>
      konamiMode
        ? "underline underline-offset-2 text-[#00FFFF] hover:text-[#FF8000]"
        : "underline underline-offset-2 text-green-400 hover:text-green-300",
  },

  // Gradient classes
  gradient: {
    text: (konamiMode: boolean) =>
      konamiMode
        ? "bg-gradient-to-r from-[#00FFFF] via-[#0080FF] to-[#FF8000] bg-clip-text text-transparent"
        : "bg-gradient-to-r from-green-400 to-green-200 bg-clip-text text-transparent",
    background: (konamiMode: boolean) =>
      konamiMode
        ? "bg-gradient-to-br from-[#0f0c29]/90 via-[#302b63]/90 to-[#00ffff]/10"
        : "bg-gradient-to-br from-green-950/50 to-black/50",
  },
};

/**
 * Animation classes
 */
export const animationClasses = {
  transition: "transition-all duration-300",
  hover: "hover:scale-[1.02]",
  focus: "focus:outline-none focus:ring-2 focus:ring-offset-2",
  pulse: (konamiMode: boolean) => (konamiMode ? "animate-pulse" : ""),
  glow: (konamiMode: boolean) => (konamiMode ? "animate-pulse" : ""),
};

/**
 * Utility function to combine theme classes
 */
export const cn = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

/**
 * Theme-aware component props
 */
export interface ThemeProps {
  konamiMode?: boolean;
  className?: string;
}
